import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Megaphone, Loader2 } from 'lucide-react'
import { releaseNotesApi } from '@/api/client'

interface ReleaseNote {
  id: number
  version: string
  title: string
  body: string
  created_by_name: string | null
  created_at: string
}

function unwrapList(res: any): ReleaseNote[] {
  const d = res?.data?.data || res?.data || []
  return Array.isArray(d) ? d : []
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function ReleaseNotesModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [notes, setNotes] = useState<ReleaseNote[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    releaseNotesApi.list(50).then(res => {
      setNotes(unwrapList(res))
      setLoading(false)
    })
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[80vh] mx-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-2">
            <Megaphone size={16} className="text-orange-400" />
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">What's New</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={18} className="animate-spin text-[var(--text-muted)]" />
            </div>
          ) : notes.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] text-center py-12">No release notes yet.</p>
          ) : (
            <div className="space-y-6">
              {notes.map((note, i) => (
                <div key={note.id}>
                  <div className="flex items-center gap-2 mb-1.5">
                    {note.version && (
                      <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400">
                        {note.version}
                      </span>
                    )}
                    <span className="text-xs text-[var(--text-muted)]">{formatDate(note.created_at)}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{note.title}</h3>
                  <div className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">
                    {note.body}
                  </div>
                  {i < notes.length - 1 && (
                    <div className="mt-6 border-t border-[var(--border-color)]" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
